import React, { useEffect, useMemo } from 'react';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { SelectAllCheckbox } from 'apps/sigma2-ui/src/components/ui/SelectAllCheckbox';

export interface ICheckbox {
  id: string;
  children: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
}

export interface CheckboxGroupProps {
  checkboxes: ICheckbox[];
  // We don't need an id since this has its own change handler
  selectAllCheckbox: Omit<ICheckbox, 'id'>;
  onChange: (params: { id: string; checked: boolean }) => void;
  onSelectAllChange: (params: { allChecked: boolean }) => void;
}

export type GetCheckboxInfo = (params: { id: string }) => {
  isDisabled: boolean;
  isChecked: boolean;
  children: React.ReactNode;
} | null;

interface UseCheckboxGroupPayload extends CheckboxGroupProps {
  getCheckboxInfo: GetCheckboxInfo;
}

export const useCheckboxGroup = ({
  initialCheckboxes,
  initialSelectAllCheckbox,
  defaultCheckboxFn,
}: {
  // We don't need an id since this has its own change handler
  initialSelectAllCheckbox: Omit<ICheckbox, 'id'>;
  initialCheckboxes: ICheckbox[];
  defaultCheckboxFn?: (item: unknown) => ICheckbox;
}): UseCheckboxGroupPayload => {
  const [checkboxes, setCheckboxes] =
    React.useState<ICheckbox[]>(initialCheckboxes);

  const onChange = React.useCallback(
    ({ id, checked }: { id: string; checked: boolean }) => {
      setCheckboxes((prevState) => {
        return prevState.map((checkbox) => {
          if (checkbox.id === id) {
            checkbox.checked = checked;
          }
          return checkbox;
        });
      });
    },
    [],
  );

  const onSelectAllChange = React.useCallback(
    ({ allChecked }: { allChecked: boolean }) => {
      setCheckboxes((prevState) => {
        return prevState.map((checkbox) => {
          checkbox.checked = allChecked;
          return checkbox;
        });
      });
    },
    [],
  );

  const selectAllCheckbox = React.useMemo(() => {
    return {
      ...initialSelectAllCheckbox,
      checked: checkboxes.every((checkbox) => checkbox.checked),
    };
  }, [checkboxes, initialSelectAllCheckbox]);

  const getCheckboxInfo = React.useCallback(
    ({ id }: { id: string }) => {
      const existingCheckbox = checkboxes.find(
        (checkbox) => checkbox.id === id,
      );

      if (existingCheckbox) {
        return {
          isChecked: existingCheckbox.checked,
          isDisabled: !!existingCheckbox.disabled,
          children: existingCheckbox.children,
        };
      }

      return null;
    },
    [checkboxes],
  );

  // Syncs new checkboxes with existing checkboxes in the effect below
  const newCheckboxes = useMemo(() => {
    if (defaultCheckboxFn) {
      return initialCheckboxes.reduce<ICheckbox[]>((acc, item) => {
        const existingCheckbox = getCheckboxInfo({ id: item.id });

        if (!existingCheckbox) {
          acc.push(defaultCheckboxFn(item));
        }

        return acc;
      }, []);
    }

    return [];
  }, [initialCheckboxes, getCheckboxInfo, defaultCheckboxFn]);

  useEffect(() => {
    if (newCheckboxes.length) {
      setCheckboxes((prevState) => {
        return [...prevState, ...newCheckboxes];
      });
    }
  }, [newCheckboxes, defaultCheckboxFn]);

  return {
    checkboxes,
    selectAllCheckbox,
    onChange,
    onSelectAllChange,
    getCheckboxInfo,
  };
};

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  selectAllCheckbox,
  checkboxes,
  onChange,
  onSelectAllChange,
}) => {
  return (
    <div>
      <SelectAllCheckbox
        checked={selectAllCheckbox.checked}
        onChange={onSelectAllChange}
      >
        {selectAllCheckbox.children}
      </SelectAllCheckbox>

      {checkboxes.map(({ id, checked, disabled, children }) => {
        return (
          <S2Checkbox
            key={id}
            checked={checked}
            disabled={disabled}
            onChange={() => onChange({ id, checked: !checked })}
          >
            {children}
          </S2Checkbox>
        );
      })}
    </div>
  );
};
